import React, { useState, useEffect } from 'react';
import {
  Box,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';

// Importar componentes genéricos
import {
  VirtualizedContainer,
  SearchFilters,
  GridCard,
  PaginationInfo
} from '../../components/VirtualizedGrid';

// Importar hook genérico
import { useVirtualizedGrid } from '../../hooks/useVirtualizedGrid';

// Importar configuración específica de infracciones
import {
  infraccionesGridConfig,
  infraccionesFilterFields,
  renderInfraccionCard
} from './InfraccionesVirtualizedConfig';

// Importar componentes específicos
import FloatingAddButton from '../../components/FloatingAddButton';
import { policiaMunicipalApi, MultaPolicia } from '../../services/policiaMunicipalApi';
import RegistroMultaPolicia from '../PoliciaMunicipal/Ventanas/RegistroMultaPolicia';
import DashboardInfracciones from './DashboardInfracciones';

const InfraccionesTransitoVirtualized: React.FC = () => {
  const [infracciones, setInfracciones] = useState<MultaPolicia[]>([]);
  const [modalRegistroOpen, setModalRegistroOpen] = useState(false);
  const [modalVisualizarOpen, setModalVisualizarOpen] = useState(false);
  const [infraccionSeleccionada, setInfraccionSeleccionada] = useState<MultaPolicia | null>(null);
  const [modoModal, setModoModal] = useState<'new' | 'edit' | 'view'>('new');
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Usar el hook genérico
  const {
    visibleItems,
    totalFiltered,
    pagination,
    filters,
    hasActiveFilters,
    updateFilter,
    clearFilters,
    goToPage,
    nextPage,
    prevPage
  } = useVirtualizedGrid(infracciones, infraccionesGridConfig);

  // Cargar infracciones
  const fetchInfracciones = async () => {
    setLoading(true);
    try {
      const data = await policiaMunicipalApi.getMultas();
      // Ordenar por fecha de modificación más reciente primero
      const sortedData = data.sort((a, b) => {
        const fechaA = new Date(a.actualizadaEn || a.creadaEn || '').getTime();
        const fechaB = new Date(b.actualizadaEn || b.creadaEn || '').getTime();
        return fechaB - fechaA;
      });
      setInfracciones(sortedData);
    } catch (error) {
      toast({
        title: 'Error al cargar infracciones',
        description: 'No se pudieron cargar las infracciones de tránsito',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInfracciones();
  }, []);

  // Manejadores
  const handleNuevaInfraccion = () => {
    setInfraccionSeleccionada(null);
    setModoModal('new');
    setModalRegistroOpen(true);
  };

  const handleEditarInfraccion = (infraccion: MultaPolicia) => {
    setInfraccionSeleccionada(infraccion);
    setModoModal('edit');
    setModalRegistroOpen(true);
  };

  const handleVisualizarInfraccion = (infraccion: MultaPolicia) => {
    setInfraccionSeleccionada(infraccion);
    setModoModal('view');
    setModalVisualizarOpen(true);
  };

  const handleGuardarInfraccion = () => {
    fetchInfracciones();
    setModalRegistroOpen(false);
    setModalVisualizarOpen(false);
    setInfraccionSeleccionada(null);
  };

  return (
    <Box p={4} pt={50} mt={8} width="100%">
      
      {/* Dashboard de estadísticas */}
      <DashboardInfracciones infracciones={infracciones} />

      {/* Filtros de búsqueda */}
      <SearchFilters
        title="Filtros de Búsqueda"
        filters={filters}
        filterFields={infraccionesFilterFields}
        hasActiveFilters={hasActiveFilters}
        totalResults={totalFiltered}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
      />

      {/* Grid virtualizado */}
      <VirtualizedContainer
        isLoading={loading}
        isEmpty={visibleItems.length === 0 && !loading}
        emptyMessage="No se encontraron infracciones"
        emptySubMessage={
          infracciones.length === 0 
            ? 'No hay infracciones registradas' 
            : 'Intenta ajustar los filtros de búsqueda'
        }
        columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
        spacing={4}
        minItemWidth="300px"
        maxItemWidth="350px"
        minItemHeight="400px"
        skeletonType="infracciones"
      >
        {visibleItems.map((infraccion, index) => (
          <GridCard
            key={infraccion.id}
            item={infraccion}
            index={index}
            renderContent={(item) => renderInfraccionCard(
              item, 
              index, 
              handleEditarInfraccion,
              handleVisualizarInfraccion
            )}
            onClick={() => handleVisualizarInfraccion(infraccion)}
            minWidth="300px"
            maxWidth="350px"
            minHeight="400px"
          />
        ))}
      </VirtualizedContainer>

      {/* Paginación */}
      <PaginationInfo
        pagination={pagination}
        onPageChange={goToPage}
        onNextPage={nextPage}
        onPrevPage={prevPage}
        showInfo={true}
      />

      {/* Botón flotante para nueva infracción */}
      <FloatingAddButton 
        text="Nueva Infracción" 
        onClick={handleNuevaInfraccion}
      />

      {/* Modal para registro/edición */}
      <Modal 
        isOpen={modalRegistroOpen} 
        onClose={() => setModalRegistroOpen(false)}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader 
            fontSize="2xl" 
            bgImage="url('/img/bg.webp')"
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            color="white" 
            textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            fontWeight="bold"
            borderTopRadius="md"
            position="relative"
          >
            {modoModal === 'new' ? 'Nueva Infracción de Tránsito' : 'Editar Infracción de Tránsito'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto">
            <RegistroMultaPolicia
              modo={modoModal}
              data={infraccionSeleccionada || undefined}
              onSave={handleGuardarInfraccion}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal para visualización */}
      <Modal 
        isOpen={modalVisualizarOpen} 
        onClose={() => setModalVisualizarOpen(false)}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader 
            fontSize="2xl" 
            bgImage="url('/img/bg.webp')"
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            color="white" 
            textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            fontWeight="bold"
            borderTopRadius="md"
            position="relative"
          >
            Detalles de Infracción - {infraccionSeleccionada?.folio || `ID-${infraccionSeleccionada?.id}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto">
            <RegistroMultaPolicia
              modo="view"
              data={infraccionSeleccionada || undefined}
              onSave={handleGuardarInfraccion}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default InfraccionesTransitoVirtualized;